import type { SubStipulantEntry } from '../../types';
import {
  formatCurrency,
  formatCurrencyFromString,
} from '@/shared/utils/currencyFormatter';

export const RISK_STEP_MAX_NUMERIC_VALUE = 10_000_000;
export const RISK_STEP_MAX_LIVES_VALUE = 1_000;

/**
 * Categoria de vida que define o teto de Capital Individual. Mesmas chaves do
 * `riskProfileList` do payload e do `BandKey` da Api — um quarto vocabulário
 * para a mesma coisa custaria tradução mental em cada leitura.
 */
export type CapitalBand = 'partnersAndDirectors' | 'employees';

/*
 * TODO: estes limites comerciais devem vir do InsureMO (schema do produto
 * 930015), não daqui. Hoje o catálogo só expõe `limitMin`/`limitMax` da
 * cobertura Morte, que são mais frouxos que a regra comercial — a troca depende
 * de o InsureMO passar a publicar o limite comercial, não só de mudar a origem
 * da constante.
 *
 * Espelhados na Api em
 * `Api/src/policies/shared/capital-global/capital-global-risk-limits.const.ts`,
 * com as mesmas chaves de banda.
 */
export const RISK_STEP_MIN_INDIVIDUAL_CAPITAL = 5_000;

/**
 * Teto por categoria de vida. No ESCALONADO a banda vem do bloco preenchido; no
 * UNIFORME vem do Tipo de Perfil, e "Colaboradores + Sócios" cai no teto de
 * colaboradores — ver `resolveCapitalBandFromTipoDePerfil` em
 * `validate-risk.util.ts`.
 */
export const RISK_STEP_MAX_INDIVIDUAL_CAPITAL_BY_BAND: Record<
  CapitalBand,
  number
> = {
  partnersAndDirectors: 300_000,
  employees: 200_000,
};

/** Teto da SOMA de vidas de todos os estipulantes, não de um campo isolado. */
export const RISK_STEP_MAX_TOTAL_LIVES = 500;

/**
 * Texto de apoio do campo Capital Individual: "De R$ 5.000,00 a R$ 300.000,00".
 *
 * Recebe a faixa EFETIVA já resolvida — a regra comercial da banda cruzada com os
 * limites da Morte do catálogo (`resolveEffectiveIndividualCapitalRange`) —, não a
 * banda crua. Anunciar o teto comercial quando o catálogo corta mais embaixo é a
 * mesma mentira na tela que um hint hardcoded deixaria, só que mais difícil de ver.
 */
export function formatIndividualCapitalRangeHint(range: {
  min: number;
  max: number;
}): string {
  return `De ${formatCurrency(range.min)} a ${formatCurrency(range.max)}`;
}

export function parseNum(value: string): number {
  const normalized = value.trim();
  if (!normalized) return 0;

  const sanitized = normalized.replace(/[^\d,.-]/g, '');
  const numericText = sanitized.includes(',')
    ? sanitized.replace(/\./g, '').replace(',', '.')
    : sanitized;
  const n = parseFloat(numericText);
  return isFinite(n) && n > 0 ? n : 0;
}

export function sanitizeLivesInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  const parsed = Number(digits);
  if (!Number.isFinite(parsed)) return '';

  if (parsed > RISK_STEP_MAX_LIVES_VALUE) {
    return String(RISK_STEP_MAX_LIVES_VALUE);
  }

  return String(Math.trunc(parsed));
}

export function formatRiskCapitalInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  let amount = parseInt(digits, 10) / 100;
  if (!Number.isFinite(amount) || amount <= 0) return '';

  if (amount > RISK_STEP_MAX_NUMERIC_VALUE) {
    amount = RISK_STEP_MAX_NUMERIC_VALUE;
  }

  return formatCurrencyFromString(String(Math.round(amount * 100)));
}

export function pickError(
  meta: { errors: unknown[] },
  submitAttempted: boolean
): string | undefined {
  if (!submitAttempted) return undefined;
  return meta.errors[0] as string | undefined;
}

export function resolveNextSubStipulantSequenceNumber(
  subStipulants: SubStipulantEntry[]
): number {
  const maxSequence = subStipulants.reduce((max, entry) => {
    const seq = Number(entry.sequenceNumber);
    return Number.isInteger(seq) && seq >= 1 ? Math.max(max, seq) : max;
  }, 0);
  return maxSequence + 1;
}

export function createSubStipulant(
  existingSubStipulants: SubStipulantEntry[] = []
): SubStipulantEntry {
  return {
    id: `sub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    sequenceNumber: resolveNextSubStipulantSequenceNumber(
      existingSubStipulants
    ),
    useIndividualDocument: false,
    documentNumber: '',
    cpf: '',
    name: '',
    segment: '',
    cnae: '',
    averagePartnerAge: undefined,
    averageEmployeeAge: undefined,
    partnerCount: undefined,
    employeeCount: undefined,
  };
}

export function defaultOneYearQuoteDates(): { start: string; end: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = today.toISOString().split('T')[0];
  const endDate = new Date(today);
  endDate.setFullYear(endDate.getFullYear() + 1);
  const end = endDate.toISOString().split('T')[0];
  return { start, end };
}
